import { useState, useEffect, useCallback, useRef } from 'react';
import {
  doc, setDoc, updateDoc, serverTimestamp, getFirestore,
} from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { useWorkSession } from '../../hooks/useWorkSession';

const MONO = "'Share Tech Mono', monospace";
const AUTO_CHECKOUT_KEY = uid => `smc_auto_co_${uid}`;

function todayIST() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
}

export default function GlobalAttendanceBar({ currentUser, onGoToAttendance }) {
  const db = getFirestore(getApp());
  const { session, loading } = useWorkSession(currentUser?.uid);

  const [elapsed,       setElapsed]      = useState('--:--:--');
  const [elapsedMin,    setElapsedMin]   = useState(0);
  const [checking,      setChecking]     = useState(false);
  const [checkingOut,   setCheckingOut]  = useState(false);
  const [minimised,     setMinimised]    = useState(false);
  const [error,         setError]        = useState('');
  const [autoCoHours,   setAutoCoHours]  = useState(8);
  const [showAutoCoWarn, setShowAutoCoWarn] = useState(false);
  const autoCoTimerRef = useRef(null);
  const warnTimerRef   = useRef(null);

  const isCheckedIn  = session?.isCheckedIn === true;
  const isComplete   = session?.isComplete === true;
  const checkinTime  = session?.record?.checkedInAt?.toDate?.()
    || (session?.record?.checkedInAtClient ? new Date(session.record.checkedInAtClient) : null);
  const locationName = session?.record?.locationName;

  // Load user-configured auto-checkout preference
  useEffect(() => {
    if (!currentUser?.uid) return;
    const stored = localStorage.getItem(AUTO_CHECKOUT_KEY(currentUser.uid));
    if (stored) setAutoCoHours(Number(stored));
  }, [currentUser?.uid]);

  // Live elapsed timer
  useEffect(() => {
    if (!isCheckedIn || !checkinTime) { setElapsed('--:--:--'); setElapsedMin(0); return; }
    const tick = () => {
      const diff = Math.max(0, Math.floor((Date.now() - checkinTime.getTime()) / 1000));
      const h = Math.floor(diff / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;
      setElapsed(`${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`);
      setElapsedMin(Math.floor(diff / 60));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCheckedIn, checkinTime?.getTime()]);

  // Auto-checkout logic
  useEffect(() => {
    clearTimeout(autoCoTimerRef.current);
    clearTimeout(warnTimerRef.current);
    setShowAutoCoWarn(false);
    if (!isCheckedIn || !checkinTime || autoCoHours === 0) return;

    const autoCoMs  = autoCoHours * 60 * 60 * 1000;
    const warnBeforeMs = 5 * 60 * 1000; // warn 5 min before auto-checkout
    const elapsed   = Date.now() - checkinTime.getTime();
    const timeToWarn = autoCoMs - warnBeforeMs - elapsed;
    const timeToCo   = autoCoMs - elapsed;

    if (timeToCo <= 0) {
      // Already past auto-checkout time — do it now
      doCheckout('auto-8h');
      return;
    }
    if (timeToWarn > 0) {
      warnTimerRef.current = setTimeout(() => setShowAutoCoWarn(true), timeToWarn);
    } else {
      setShowAutoCoWarn(true);
    }
    autoCoTimerRef.current = setTimeout(() => {
      setShowAutoCoWarn(false);
      doCheckout('auto-8h');
    }, timeToCo);

    return () => {
      clearTimeout(autoCoTimerRef.current);
      clearTimeout(warnTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCheckedIn, checkinTime?.getTime(), autoCoHours]);

  const doCheckout = useCallback(async (method = 'manual') => {
    if (!currentUser?.uid) return;
    const today = todayIST();
    setCheckingOut(true);
    try {
      const base     = checkinTime || new Date();
      const duration = Math.max(0, Math.round((Date.now() - base.getTime()) / 60000));
      const cap      = autoCoHours * 60;
      const logged   = cap > 0 ? Math.min(duration, cap) : duration;
      await updateDoc(doc(db, 'attendance', currentUser.uid, 'records', today), {
        checkoutTime:           serverTimestamp(),
        isOpen:                 false,
        actualDuration_minutes: duration,
        duration_minutes:       duration,
        loggedHours_minutes:    logged,
        cappingApplied:         cap > 0 && duration > cap,
        cappingNote:            cap > 0 && duration > cap ? 'Capped at company hours' : null,
        checkoutMethod:         method,
        checkoutNote:           null,
      });
    } catch (e) {
      console.error('[GlobalBar] checkout error:', e.code, e.message);
      setError(e.code === 'permission-denied' ? 'Permission denied — contact admin' : 'Checkout failed');
      setTimeout(() => setError(''), 5000);
    } finally {
      setCheckingOut(false);
    }
  }, [currentUser?.uid, checkinTime, autoCoHours, db]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCheckIn = useCallback(async () => {
    setChecking(true);
    setError('');
    const today = todayIST();
    let locName = 'Office';
    let lat = null, lng = null;
    try {
      try {
        const pos = await new Promise((res, rej) =>
          navigator.geolocation.getCurrentPosition(res, rej, { timeout: 5000 })
        );
        lat = pos.coords.latitude;
        lng = pos.coords.longitude;
        try {
          const geo = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
          ).then(r => r.json());
          locName = geo.address?.city || geo.address?.town || geo.address?.village || 'Location captured';
        } catch (_) {}
      } catch (_) {}

      let deviceType = 'web';
      try { if (window.Capacitor?.isNativePlatform?.()) deviceType = 'android'; } catch (_) {}

      await setDoc(doc(db, 'attendance', currentUser.uid, 'records', today), {
        date:                 today,
        uid:                  currentUser.uid,
        name:                 currentUser.name || '',
        role:                 currentUser.role || '',
        checkedInAt:          serverTimestamp(),
        checkedInAtClient:    new Date().toISOString(),
        note:                 '',
        location:             locName,
        locationName:         locName,
        locationSource:       lat ? 'gps' : 'manual',
        lat, lng,
        checkinLat:           lat,
        checkinLng:           lng,
        checkinAccuracy_m:    null,
        locationAccuracy_m:   null,
        deviceType,
        workType:             'office',
        companyAllottedHours: autoCoHours || 8,
        sessionReloads:       0,
        lastReloadAt:         null,
        loggedHours_minutes:  null,
        actualDuration_minutes: null,
        cappingApplied:       false,
        cappingNote:          null,
        checkoutTime:         null,
        checkoutLat:          null,
        checkoutLng:          null,
        checkoutLocationName: null,
        checkoutAccuracy_m:   null,
        checkoutGeoRule:      null,
        checkoutMethod:       null,
        duration_minutes:     null,
        isOpen:               true,
        isDeleted:            false,
      });
    } catch (err) {
      console.error('[GlobalBar] check-in error:', err.code, err.message);
      setError(err.code === 'permission-denied' ? 'Permission denied — contact admin' : 'Check-in failed');
      setTimeout(() => setError(''), 5000);
    } finally {
      setChecking(false);
    }
  }, [currentUser, db, autoCoHours]); // eslint-disable-line react-hooks/exhaustive-deps

  // Hide on login/guest pages
  if (!currentUser?.uid || !currentUser?.role) return null;
  if (loading) return null;

  // ── Past 10:00 AM IST with no check-in → red alert ───────────────────────
  const currentISTHour = parseInt(
    new Date().toLocaleString('en-CA', { timeZone: 'Asia/Kolkata', hour: 'numeric', hour12: false })
  );
  const isLateCheckin = session?.notCheckedIn && currentISTHour >= 10;

  // ── 4h+ threshold: switch to amber urgent mode ────────────────────────────
  const FOUR_H_MIN  = 4 * 60;
  const isUrgent    = isCheckedIn && elapsedMin >= FOUR_H_MIN;
  const barColor    = isCheckedIn
    ? (isUrgent ? '#f59e0b' : '#1D9E75')
    : isLateCheckin ? '#ef4444'
    : isComplete ? '#4d5a6e' : '#4d5a6e';

  // ── Minimised pill ────────────────────────────────────────────────────────
  if (minimised) {
    return (
      <div onClick={() => setMinimised(false)} style={{
        position:'fixed', bottom:16, right:16, zIndex:999,
        width:48, height:48, borderRadius:'50%',
        background: isCheckedIn ? (isUrgent ? 'rgba(245,158,11,0.15)' : '#1D9E75') : 'var(--smc-panel)',
        border:`0.5px solid ${barColor}60`,
        display:'flex', alignItems:'center', justifyContent:'center',
        cursor:'pointer', boxShadow:'0 4px 16px rgba(0,0,0,0.30)',
      }}>
        <style>{`@keyframes attendPulse{0%,100%{opacity:1}50%{opacity:0.35}}`}</style>
        {isCheckedIn
          ? <div style={{ width:8, height:8, borderRadius:'50%', background: isUrgent ? '#f59e0b' : '#fff', animation:'attendPulse 2s infinite' }} />
          : <span style={{ fontSize:18 }}>📍</span>
        }
      </div>
    );
  }

  // ── Full bar ──────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`@keyframes attendPulse{0%,100%{opacity:1}50%{opacity:0.35}}`}</style>

      {/* Auto-checkout warning banner */}
      {showAutoCoWarn && (
        <div style={{
          position:'fixed', bottom:'calc(50px + env(safe-area-inset-bottom, 0px))', left:0, right:0, zIndex:1000,
          background:'rgba(245,158,11,0.12)', borderTop:'1px solid rgba(245,158,11,0.4)',
          display:'flex', alignItems:'center', gap:12, padding:'8px 16px',
          fontFamily:MONO, fontSize:10, color:'#f59e0b',
        }}>
          <span>⏰</span>
          <span style={{ flex:1 }}>Auto-checkout in 5 minutes ({autoCoHours}h limit reached). Still working?</span>
          <button onClick={() => { clearTimeout(autoCoTimerRef.current); setShowAutoCoWarn(false); }}
            style={{ fontFamily:MONO, fontSize:9, padding:'4px 10px', cursor:'pointer',
              background:'rgba(245,158,11,0.15)', border:'1px solid rgba(245,158,11,0.4)',
              color:'#f59e0b' }}>
            STILL HERE
          </button>
          <button onClick={() => { setShowAutoCoWarn(false); doCheckout('manual'); }}
            style={{ fontFamily:MONO, fontSize:9, padding:'4px 10px', cursor:'pointer',
              background:'transparent', border:'1px solid rgba(226,75,74,0.4)', color:'#f87171' }}>
            CHECK OUT
          </button>
        </div>
      )}

      <div className="smc-attendance-bar" style={{
        position:'fixed', bottom:0, left:0, right:0, zIndex:999,
        background: isUrgent ? 'rgba(245,158,11,0.08)' : isLateCheckin ? 'rgba(239,68,68,0.06)' : 'var(--smc-deep, #060b18)',
        borderTop:`0.5px solid ${barColor}40`,
        display:'flex', alignItems:'center', gap:10,
        padding:`0 16px 0 16px`,
        paddingBottom: 'max(0px, env(safe-area-inset-bottom, 0px))',
        height: 'calc(50px + env(safe-area-inset-bottom, 0px))',
        boxShadow:'0 -4px 20px rgba(0,0,0,0.25)',
        transition:'background 0.4s',
      }}>
        {/* Status dot */}
        <div style={{
          width:8, height:8, borderRadius:'50%', background:barColor, flexShrink:0,
          animation: isCheckedIn ? 'attendPulse 2s infinite' : 'none',
        }} />

        {/* Left info */}
        {isCheckedIn ? (
          <>
            <div className="smc-att-timer" style={{ fontFamily:MONO, fontSize: isUrgent ? 16 : 20, fontWeight:700,
              color:barColor, letterSpacing:'0.04em', lineHeight:1 }}>
              {elapsed}
            </div>
            {isUrgent && (
              <div style={{ fontFamily:MONO, fontSize:9, color:'#f59e0b',
                background:'rgba(245,158,11,0.12)', border:'1px solid rgba(245,158,11,0.3)',
                padding:'2px 8px', whiteSpace:'nowrap' }}>
                {elapsedMin >= autoCoHours * 60 ? `AUTO-OUT SOON` : '4H+'}
              </div>
            )}
            {locationName && !isUrgent && (
              <div className="smc-att-location" style={{ fontSize:11, color:'var(--smc-tx3)', overflow:'hidden',
                textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:140 }}>
                📍 {locationName}
              </div>
            )}
          </>
        ) : isComplete ? (
          <div style={{ fontSize:11, color:'var(--smc-tx3)', fontFamily:MONO }}>
            ✓ CHECKED OUT TODAY
          </div>
        ) : isLateCheckin ? (
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <div style={{
              width:7, height:7, borderRadius:'50%', background:'#ef4444', flexShrink:0,
              animation:'attendPulse 1.2s infinite',
            }} />
            <div style={{ fontSize:11, color:'#ef4444', fontFamily:MONO, letterSpacing:'0.04em', fontWeight:700 }}>
              NOT CHECKED IN · CHECK IN NOW
            </div>
          </div>
        ) : (
          <div style={{ fontSize:11, color:'var(--smc-tx3)', fontFamily:MONO, letterSpacing:'0.04em' }}>
            NOT CHECKED IN TODAY
          </div>
        )}

        {error && (
          <div style={{ fontSize:10, color:'#E24B4A', background:'rgba(226,75,74,0.10)',
            padding:'3px 8px', borderRadius:5, border:'0.5px solid rgba(226,75,74,0.25)', fontFamily:MONO }}>
            {error}
          </div>
        )}

        <div className="smc-att-spacer" style={{ flex:1 }} />

        {/* Right actions */}
        <div className="smc-att-actions" style={{ display:'flex', gap:7, alignItems:'center' }}>
          <button onClick={() => onGoToAttendance?.()}
            style={{ fontSize:10, color:'var(--smc-tx3)', cursor:'pointer', fontFamily:MONO,
              padding:'4px 8px', borderRadius:5, border:'0.5px solid var(--smc-bd)', background:'transparent' }}>
            Full view
          </button>

          {!isCheckedIn && !isComplete ? (
            <button onClick={handleCheckIn} disabled={checking}
              style={{ padding:'7px 18px', borderRadius:8,
                background: isLateCheckin ? '#ef4444' : 'var(--smc-ac, #0e9cc0)',
                color:'#fff', border:'none', fontSize:12, fontWeight:700,
                cursor:checking ? 'wait' : 'pointer', opacity:checking ? 0.7 : 1,
                fontFamily:MONO, letterSpacing:'0.03em',
                animation: isLateCheckin && !checking ? 'attendPulse 2s infinite' : 'none',
              }}>
              {checking ? 'Checking in...' : isLateCheckin ? '⚠ Check In Now' : '→ Check in'}
            </button>
          ) : isCheckedIn ? (
            <button onClick={() => doCheckout('manual')} disabled={checkingOut}
              style={{ padding:'7px 18px', borderRadius:8,
                background: isUrgent ? 'rgba(245,158,11,0.15)' : 'rgba(226,75,74,0.12)',
                color: isUrgent ? '#f59e0b' : '#E24B4A',
                border:`0.5px solid ${isUrgent ? 'rgba(245,158,11,0.40)' : 'rgba(226,75,74,0.30)'}`,
                fontSize: isUrgent ? 12 : 11, fontWeight: isUrgent ? 700 : 500,
                cursor:checkingOut ? 'wait' : 'pointer', opacity:checkingOut ? 0.7 : 1,
                fontFamily:MONO }}>
              {checkingOut ? '…' : isUrgent ? '⏏ CHECK OUT NOW' : 'Check out'}
            </button>
          ) : null}

          <button onClick={() => setMinimised(true)} title="Minimise"
            style={{ width:28, height:28, borderRadius:6, background:'transparent',
              border:'0.5px solid var(--smc-bd)', display:'flex', alignItems:'center',
              justifyContent:'center', cursor:'pointer', color:'var(--smc-tx3)',
              fontSize:14, flexShrink:0 }}>
            —
          </button>
        </div>
      </div>
    </>
  );
}
